import React, { forwardRef, useCallback, useMemo } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Grid, FormControlLabel, Switch, Tooltip } from '@mui/material';
import { TextField } from '@totalsoft/rocket-ui';
import { withRights } from 'providers/RightsConsumer';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import { useTranslation } from 'react-i18next';
import { withParams } from 'providers/ParamsConsumer';
import { TP_TEXT } from 'constants/inputTypes';

const ChassisNumberIconComponent = ({ onAssetDataChange, chassisNumber, onPropertyChange, rights, params }) => {
  const [t] = useTranslation();

  const eurotaxIntegration = useMemo(() => params.assetHierarchySources.find(x => x.name === 'Eurotax'), [params]);
  const charismaAssetSearchIntegration = useMemo(
    () => params.assetIntegrationFeatures.find(x => x.featureName === 'AssetSearch'),
    [params]
  );

  return rights?.leasingVehicleSearch && (eurotaxIntegration?.activeYN || charismaAssetSearchIntegration?.enabled) ? (
    <Tooltip title={t('OfferEdit.Asset.SearchChassisNumber')}>
      <DriveEtaIcon
        id='search_chassisNumber'
        onClick={() => {
          onPropertyChange('chassisNumber', chassisNumber);
          onAssetDataChange(chassisNumber);
        }}
      />
    </Tooltip>
  ) : (
    <></>
  );
};

ChassisNumberIconComponent.propTypes = {
  onPropertyChange: PropTypes.func.isRequired,
  chassisNumber: PropTypes.string,
  onAssetDataChange: PropTypes.func,
  rights: PropTypes.object.isRequired,
  params: PropTypes.array
};

const ChassisNumberIcon = compose(withParams, withRights)(ChassisNumberIconComponent);
const OfferAssetChassisNumberComponent = React.memo(
  ({
    t,
    chassisNumberError,
    rights,
    onPropertyChange,
    isChassisNumberAvailable,
    chassisNumber,
    onAssetDataChange,
    processing,
    chassisNumberTooltip,
    chassisNumberDisabled,
    addToRefs
  }) => {
    const handleChange = useCallback(
      value => {
        onPropertyChange('chassisNumber', value);
      },
      [onPropertyChange]
    );

    return (
      <Grid container spacing={3} alignItems='center'>
        <Grid item xs={12} sm={6} md={4}>
          <Tooltip title={chassisNumberTooltip}>
            <FormControlLabel
              control={
                <Switch
                  id={'offerAssetCard_chassis_isChassisNumberAvailable'}
                  checked={isChassisNumberAvailable || false}
                  onChange={onCheckBoxChange(onPropertyChange('isChassisNumberAvailable'))}
                  disabled={rights.readOnlyFull || !rights.financedAssetWrite || processing || chassisNumberDisabled}
                />
              }
              label={t('OfferEdit.Asset.Info.ChassisNumberAvailable')}
            />
          </Tooltip>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          my={2.5}
          id={t('OfferEdit.Asset.Info.ChassisNumber')}
          ref={ref => addToRefs(ref, t('OfferEdit.Asset.Info.ChassisNumber'), TP_TEXT)}
        >
          {isChassisNumberAvailable && (
            <TextField
              id={'offerAssetCard_chassis_chassisNumber'}
              required
              fullWidth
              error={Boolean(chassisNumberError)}
              helperText={chassisNumberError}
              value={chassisNumber}
              onChange={handleChange}
              label={t('OfferEdit.Asset.Info.ChassisNumber')}
              disabled={rights.readOnlyFull || !rights.financedAssetWrite || processing}
              endAdornment={
                <ChassisNumberIcon
                  onAssetDataChange={onAssetDataChange}
                  chassisNumber={chassisNumber}
                  onPropertyChange={onPropertyChange}
                />
              }
            />
          )}
        </Grid>
      </Grid>
    );
  }
);

OfferAssetChassisNumberComponent.displayName = 'OfferAssetChassisNumberComponent';

OfferAssetChassisNumberComponent.propTypes = {
  t: PropTypes.func.isRequired,
  rights: PropTypes.object.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  isChassisNumberAvailable: PropTypes.bool,
  chassisNumber: PropTypes.string,
  chassisNumberError: PropTypes.string,
  onAssetDataChange: PropTypes.func.isRequired,
  processing: PropTypes.bool,
  chassisNumberTooltip: PropTypes.string,
  chassisNumberDisabled: PropTypes.bool,
  addToRefs: PropTypes.func.isRequired
};

OfferAssetChassisNumberComponent.defaultProps = {
  chassisNumberTooltip: '',
  chassisNumberDisabled: false
};

const MemoizedOfferAssetChassisNumberComponent = withTranslation()(withRights(OfferAssetChassisNumberComponent));

const ForwardedOfferAssetChassisNumberComponent = forwardRef((props, ref) => (
  <MemoizedOfferAssetChassisNumberComponent {...props} forwardedRef={ref} />
));

ForwardedOfferAssetChassisNumberComponent.displayName = 'ForwardedOfferAssetChassisNumberComponent';

export default ForwardedOfferAssetChassisNumberComponent;
